import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";

import {
  addNoteCancelFeedback,
  cancelSubscription,
  downsellSubscription,
  getSubscriptionDetails,
  pauseSubscription,
  retryPayment,
  undoCancelSubscription,
  unPauseSubscription,
} from "./subscriptionAPI";
import { addPdfUpsellAsync } from "../admin/adminSlice";
import { logoutAsync } from "../authentication/authenticationSlice";

export interface PrimarySubscription {
  isPrimary: boolean;
  active: boolean;
  price: number;
  billingInterval: number;
  billingDescriptor: string;
  cancelDate?: string;
  blueSnapMember: boolean;
  productName: string;
  peopleReportThreshold: number;
  propertyReportThreshold: number;
  phoneReportThreshold: number;
  socialReportThreshold: number;
  vehicleReportThreshold: number;
  subscriptionId: number;
  isInRebill: boolean;
  showSecondaryAccelerate?: boolean;
  showAccelerateBanner?: boolean;
  accelerateBilling?: boolean;
  accelerateReportType?: "people" | "vehicle" | "property" | "social" | "phone";
  productSku?: string;
}
export interface TrialDetails {
  peopleReportThreshold?: number;
  propertyReportThreshold?: number;
  phoneReportThreshold?: number;
  socialReportThreshold?: number;
  vehicleReportThreshold?: number;
}
export interface IDPSubscription {
  active: boolean;
  price: number;
  billingDate: string;
}

export interface DownsellDetails {
  downSellPrice: number;
  downSellProductId: number;
  isDownsold: boolean;
  downSellPeopleReportThreshold: number;
  downSellPropertyReportThreshold: number;
  downSellPhoneReportThreshold: number;
  downSellSocialReportThreshold: number;
  downSellVehicleReportThreshold: number;
}

export interface PauseDetails {
  isCurrentlyPaused: boolean;
  pauseAccessDate: string;
  pauseBillingDate: string;
}

export interface SubscriptionDetails {
  status: "idle" | "loading" | "failed";
  downsellDetails?: DownsellDetails;
  primarySubscription?: PrimarySubscription;
  trialDetails?: TrialDetails;
  idpSubscription?: IDPSubscription;
  pauseDetails?: PauseDetails;
  isInTrial?: boolean;
  showReactivateConfirmation: boolean;
  hasPdfUnlimited?: boolean;
}

const initialState: SubscriptionDetails = {
  status: "idle",
  showReactivateConfirmation: false,
};

export const getSubscriptionDetailsAsync = createAsyncThunk<any, void, {}>(
  "subscription/getSubscriptionDetails",
  async () => {
    return getSubscriptionDetails();
  },
);

// used on cancel-feedback page to send reason and feedback, no state updates
export const sendNoteCancelFeedbackAsync = createAsyncThunk<
  any,
  { content: string },
  {}
>("subscription/sendNoteCancelFeedback", async ({ content }) => {
  return addNoteCancelFeedback({ content });
});

export const downsellSubscriptionAsync = createAsyncThunk<any, void, {}>(
  "subscription/downsellSubscription",
  async () => {
    return downsellSubscription();
  },
);

export const cancelSubscriptionAsync = createAsyncThunk<any, void, {}>(
  "subscription/cancelSubscription",
  async () => {
    return cancelSubscription();
  },
);

export const retryPaymentAsync = createAsyncThunk<
  any,
  { email: string | undefined; IgnoreBillingDateCheck: boolean | undefined },
  {}
>("subscription/retryPayment", async ({ email, IgnoreBillingDateCheck }) => {
  return retryPayment({ email, IgnoreBillingDateCheck });
});

export const pauseSubscriptionAsync = createAsyncThunk<any, void, {}>(
  "subscription/pauseSubscription",
  async () => {
    return pauseSubscription();
  },
);

export const unPauseSubscriptionAsync = createAsyncThunk<any, void, {}>(
  "subscription/unPauseSubscription",
  async () => {
    return unPauseSubscription();
  },
);

export const undoCancelSubscriptionAsync = createAsyncThunk<any, void, {}>(
  "subscription/undoCancelSubscription",
  async () => {
    return undoCancelSubscription();
  },
);

export const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    updateIsInRebill: state => {
      if (state.primarySubscription) {
        state.primarySubscription.isInRebill = false;
      }
    },
    setAccelerateBilling: state => {
      if (state.primarySubscription) {
        state.primarySubscription.accelerateBilling = true;
      }
    },
    unsetAccelerateBilling: state => {
      if (state.primarySubscription) {
        state.primarySubscription.accelerateBilling = false;
      }
    },
    setShowReactivateConfirmation: (state, action) => {
      state.showReactivateConfirmation = action.payload;
    },
    setShowSecondaryAccelerate: (state, action) => {
      if (state.primarySubscription) {
        state.primarySubscription.showSecondaryAccelerate = action.payload;
      }
    },
    setShowAccelerateBanner: (state, action) => {
      if (state.primarySubscription) {
        state.primarySubscription.showAccelerateBanner = action.payload;
      }
    },
  },
  extraReducers: builder => {
    builder
      /* getSubscriptionDetailsAsync */
      .addCase(getSubscriptionDetailsAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(getSubscriptionDetailsAsync.fulfilled, (state, action) => {
        state.status = "idle";

        // set primary subscription on state
        if (action.payload.primarySubscription) {
          state.primarySubscription = {
            isPrimary: action.payload.primarySubscription.isPrimary,
            active: action.payload.primarySubscription.active,
            price: action.payload.primarySubscription.price,
            billingInterval: action.payload.primarySubscription.billingInterval,
            billingDescriptor:
              action.payload.primarySubscription.billingDescriptor,
            cancelDate: action.payload.primarySubscription.cancelDate,
            blueSnapMember: action.payload.primarySubscription.blueSnapMember,
            productName: action.payload.primarySubscription.productName,
            peopleReportThreshold:
              action.payload.primarySubscription.peopleReportThreshold,
            propertyReportThreshold:
              action.payload.primarySubscription.propertyReportThreshold,
            phoneReportThreshold:
              action.payload.primarySubscription.phoneReportThreshold,
            socialReportThreshold:
              action.payload.primarySubscription.socialReportThreshold,
            vehicleReportThreshold:
              action.payload.primarySubscription.autoReportThreshold,
            subscriptionId: action.payload.primarySubscription.subscriptionId,
            isInRebill: action.payload.primarySubscription.isInRebill,
            productSku: action.payload.primarySubscription.productSku,
          };
        }

        if (action.payload.trialDetails) {
          state.trialDetails = action.payload.trialDetails;
        }

        // set id protect subscription on state
        if (action.payload.idpSubscription) {
          state.idpSubscription = {
            active: action.payload.idpSubscription.active,
            price: action.payload.idpSubscription.price,
            billingDate: action.payload.idpSubscription.billingDate,
          };
        }

        // set downsell price on state
        state.downsellDetails = action.payload.downsellData && {
          downSellPrice: action.payload.downsellData.downsellPrice,
          downSellProductId: action.payload.downsellData.downsellProductId,
          isDownsold: action.payload.downsellData.isDownsold,
          downSellPeopleReportThreshold:
            action.payload.downsellData.downsellPeopleReportThreshold,
          downSellPropertyReportThreshold:
            action.payload.downsellData.downsellPropertyReportThreshold,
          downSellPhoneReportThreshold:
            action.payload.downsellData.downsellPhoneReportThreshold,
          downSellSocialReportThreshold:
            action.payload.downsellData.downsellSocialReportThreshold,
          downSellVehicleReportThreshold:
            action.payload.downsellData.downsellVehicleReportThreshold,
        };

        // set pause details on state
        state.pauseDetails = {
          isCurrentlyPaused:
            action.payload.pauseDetails &&
            action.payload.pauseDetails.isCurrentlyPaused,
          pauseAccessDate:
            action.payload.pauseDetails &&
            action.payload.pauseDetails.pauseAccessDate,
          pauseBillingDate:
            action.payload.pauseDetails &&
            action.payload.pauseDetails.pauseBillingDate,
        };

        // set isInTrial on state
        state.isInTrial = action.payload.isInTrial;

        //set hasPdfUnlimited on state
        state.hasPdfUnlimited = action.payload.hasPdfUnlimited;
      })
      .addCase(getSubscriptionDetailsAsync.rejected, state => {
        state.status = "failed";
      })
      /* retry Payment */
      .addCase(retryPaymentAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(retryPaymentAsync.fulfilled, (state, action) => {
        state.status = "idle";
        //if is a duplicate retry will set isInRebill to false to close dashboard modal
        if (action.payload.duplicateRetry && state.primarySubscription) {
          state.primarySubscription.isInRebill = false;
        }
      })
      .addCase(retryPaymentAsync.rejected, state => {
        state.status = "failed";
      })
      .addCase(undoCancelSubscriptionAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(undoCancelSubscriptionAsync.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload) {
          state.showReactivateConfirmation = true;
        }
      })
      .addCase(undoCancelSubscriptionAsync.rejected, state => {
        state.status = "failed";
      })
      /* downsell */
      .addCase(downsellSubscriptionAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(downsellSubscriptionAsync.fulfilled, state => {
        state.status = "idle";
        if (state.downsellDetails) {
          state.downsellDetails.isDownsold = true;
        }
      })
      .addCase(downsellSubscriptionAsync.rejected, state => {
        state.status = "failed";
      })
      /* handle pdfUpsell */
      .addCase(addPdfUpsellAsync.fulfilled, state => {
        state.hasPdfUnlimited = true;
      })
      /* handle logout */
      .addCase(logoutAsync.pending, () => {})
      .addCase(logoutAsync.fulfilled, state => {
        state.primarySubscription = undefined;
        state.downsellDetails = undefined;
        state.idpSubscription = undefined;
        state.pauseDetails = undefined;
        state.isInTrial = undefined;
        state.hasPdfUnlimited = undefined;
        state.showReactivateConfirmation = false;
        state.status = "idle";
      })
      .addCase(logoutAsync.rejected, () => {});
  },
});

/* actions */
export const {
  updateIsInRebill,
  setAccelerateBilling,
  unsetAccelerateBilling,
  setShowReactivateConfirmation,
  setShowSecondaryAccelerate,
  setShowAccelerateBanner,
} = subscriptionSlice.actions;

/* Getters */
export const selectPrimarySubscription = (state: RootState) =>
  state.subscription.primarySubscription;

export const selectTrialDetails = (state: RootState) =>
  state.subscription.trialDetails;

export const selectIDPSubscription = (state: RootState) =>
  state.subscription.idpSubscription;

export const selectDownsellDetails = (state: RootState) =>
  state.subscription.downsellDetails;

export const selectSubscriptionStatus = (state: RootState) =>
  state.subscription.status;

export const selectPauseDetails = (state: RootState) =>
  state.subscription.pauseDetails;

export const selectShowReactivateConfirmation = (state: RootState) =>
  state.subscription.showReactivateConfirmation;

export const selectIsInTrial = (state: RootState) =>
  state.subscription.isInTrial;

export const selectIsInRebill = (state: RootState) =>
  state.subscription.primarySubscription?.isInRebill;

export const selectHasPdfUnlimited = (state: RootState) =>
  state.subscription.hasPdfUnlimited;

export const selectShowSecondaryAccelerate = (state: RootState) =>
  state.subscription.primarySubscription?.showSecondaryAccelerate;

export const selectShowAccelerateBanner = (state: RootState) =>
  state.subscription.primarySubscription?.showAccelerateBanner;

export default subscriptionSlice.reducer;
